import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const VISIT_COUNT_KEY = 'pwa-visit-count';
const DISMISSED_KEY = 'pwa-install-dismissed';
const INSTALLED_KEY = 'pwa-installed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(() => {
    if (typeof window === 'undefined') return false;
    // Check if already installed (standalone mode or localStorage flag)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const installedFlag = localStorage.getItem(INSTALLED_KEY) === 'true';
    return isStandalone || installedFlag;
  });
  
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (!dismissedAt) return false;
    const dismissedTime = parseInt(dismissedAt, 10);
    return Date.now() - dismissedTime < DISMISS_DURATION_MS;
  });

  const [visitCount, setVisitCount] = useState(() => {
    if (typeof window === 'undefined') return 0;
    return parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
  });

  // Track visits
  useEffect(() => {
    const newCount = visitCount + 1;
    localStorage.setItem(VISIT_COUNT_KEY, newCount.toString());
    setVisitCount(newCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Listen for the beforeinstallprompt event
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const appInstalledHandler = () => {
      setIsInstalled(true);
      localStorage.setItem(INSTALLED_KEY, 'true');
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', appInstalledHandler);

    // Check for standalone mode changes (with Safari <14 fallback)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const mediaHandler = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setIsInstalled(true);
        localStorage.setItem(INSTALLED_KEY, 'true');
      }
    };
    // Safari <14 only supports deprecated addListener/removeListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', mediaHandler);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(mediaHandler);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', appInstalledHandler);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', mediaHandler);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(mediaHandler);
      }
    };
  }, []);

  const canInstall = !isInstalled && !isDismissed && deferredPrompt !== null;
  const shouldShowPrompt = canInstall && visitCount >= 2;

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        localStorage.setItem(INSTALLED_KEY, 'true');
        setDeferredPrompt(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error prompting PWA install:', error);
      return false;
    }
  }, [deferredPrompt]);

  const dismissInstall = useCallback(() => {
    setIsDismissed(true);
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
  }, []);

  const resetDismissal = useCallback(() => {
    setIsDismissed(false);
    localStorage.removeItem(DISMISSED_KEY);
  }, []);

  return {
    canInstall,
    shouldShowPrompt,
    isInstalled,
    isDismissed,
    visitCount,
    promptInstall,
    dismissInstall,
    resetDismissal,
  };
}
